// Trusted Types default policy MUST be registered before anything else can
// promote a script URL. Keep this as the very first import. See the file
// header for the CSP context.
import './trusted-types-policy';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import './styles/globals.css';
import './i18n';

// Manual service worker registration. vite-plugin-pwa's auto-injected
// registerSW.js is disabled in vite.config (injectRegister: false) because
// it runs as a separate script tag and could fire BEFORE the Trusted Types
// default policy is installed by the trusted-types-policy import above.
// Registering here, after that import, guarantees the order so the CSP's
// `require-trusted-types-for 'script'` directive does not block
// navigator.serviceWorker.register('/sw.js').
//
// sw.ts handles its own update lifecycle (skipWaiting on install, activate
// handler clears stale caches), so we do not need vite-plugin-pwa's
// registerSW wrapper from `virtual:pwa-register` (which would pull in
// workbox-window as a runtime dep without functional benefit here).
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[chlann] service worker registration failed:', err);
      }
    });
  });
}

// Sentry was initialised here behind `if (VITE_SENTRY_DSN)`. It has been
// removed rather than fixed, because it could never have worked: the CSP in
// vercel.json allows no Sentry ingest origin in connect-src, so every event
// the SDK tried to send would have been blocked. No Sentry project exists yet
// either (.claude/TODO.md still lists creating one), so the DSN has never been
// set in any environment and the init has never run in production.
//
// Meanwhile the top-level `import * as Sentry` pulled the SDK plus the replay
// integration into the entry chunk on every page load, paying the bytes for a
// feature that was switched off.
//
// To wire error tracking up later: create the project, add @sentry/react back,
// restore this init, AND add the project's ingest origin to connect-src in
// vercel.json in the same change. Doing the first three without the fourth
// reproduces exactly the silent breakage this removal clears.

// Get the root element
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

// Detect "stale bundle after deploy" error: the entry bundle the browser
// is running references chunk hashes that no longer exist on the CDN.
// Vercel responds with index.html (HTML) for the missing /assets/*.js,
// and the browser refuses to execute it. Pattern matches both Vite and
// generic dynamic-import failures.
function isStaleBundleError(err?: Error): boolean {
  if (!err) return false;
  const msg = err.message || '';
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Loading CSS chunk \d+ failed/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /MIME type of "text\/html"/i.test(msg)
  );
}

// Fallback UI for error boundary
function ErrorFallback({ error, componentStack }: { error?: Error; componentStack?: string }) {
  // Log error details to console for debugging in dev only. In production
  // replaying the stack into the user's devtools console
  // adds no operator value and leaks component-tree internals plus any
  // user-content fragments that ended up in the error message.
  if (error) {
    if (import.meta.env.DEV) {
      console.error('[Chlann ErrorBoundary]', error.message, error.stack);
      if (componentStack) console.error('[Component Stack]', componentStack);
    } else {
      console.warn('[Chlann] application error - reload to recover');
    }
  }

  // Stale-bundle auto-recovery. Use a sessionStorage flag to guard against
  // an infinite reload loop if the issue isn't actually a stale bundle.
  if (typeof window !== 'undefined' && isStaleBundleError(error)) {
    const FLAG = 'chlann-stale-bundle-reload-attempted';
    if (!sessionStorage.getItem(FLAG)) {
      sessionStorage.setItem(FLAG, '1');
      // Unregister any service worker first - it's the most common holder
      // of the stale entry HTML. Then hard-reload.
      const reload = () => { window.location.reload(); };
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker
          .getRegistrations()
          .then((regs) => Promise.all(regs.map((r) => r.unregister())))
          .catch(() => {})
          .finally(reload);
      } else {
        reload();
      }
      return (
        <div className="flex min-h-screen items-center justify-center bg-surface-1 text-ink-2">
          <p className="text-ink-3 text-sm">Athnuachan… / Refreshing…</p>
        </div>
      );
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-1 text-ink-2">
      <div className="text-center max-w-md px-4">
        <div className="mb-4 text-6xl">😔</div>
        <h1 className="font-display text-xl font-bold mb-2 text-ink-1">Chuaigh rud éigin mícheart / Something went wrong</h1>
        <p className="text-ink-3 mb-4">
          Earráid gan choinne. Athnuaigh an leathanach le triail eile a bhaint as.
          <br />
          <span className="text-ink-3 text-sm">An unexpected error occurred. Please refresh the page to try again.</span>
        </p>
        <button
          type="button"
          onClick={() => { window.location.reload(); }}
          className="px-4 py-2 bg-primary hover:bg-primary-hover text-primary-on rounded-lg transition-colors"
        >
          Athnuaigh / Refresh
        </button>
      </div>
    </div>
  );
}

// Top-level crash boundary. This was Sentry.ErrorBoundary; the Sentry SDK has
// been removed (see the note above), but the boundary itself is load-bearing
// even without it, because it is what puts ErrorFallback on screen instead of
// a blank page when a render throws. Its stale-bundle branch is the recovery
// path for a deploy landing mid-session, so losing it would be a visible
// regression rather than a reporting one.
class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error?: Error; componentStack?: string }
> {
  state: { error?: Error; componentStack?: string } = {};

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ error, componentStack: info.componentStack ?? undefined });
  }

  render() {
    if (this.state.error) {
      return <ErrorFallback error={this.state.error} componentStack={this.state.componentStack} />;
    }
    return this.props.children;
  }
}

// Create the root and render the app
ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </AppErrorBoundary>
  </React.StrictMode>
);
